import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DefaultCard from '../../components/default/DefaultCard';
import PageHeader from '../../components/PageHeader/PageHeader';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import LazyLoading from '../../components/LazyLoading';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useChecklistService from '../../app/services/hooks/useChecklistService';
import '../../styles/page-components.css';

interface Answer {
    id: number;
    question_id: number;
    question_text: string;
    selected_option_text: string;
    score_obtained: number;
    max_score: number;
    allow_multiple: boolean;
}

interface RecommendedService {
    id: string;
    name: string;
    description: string;
    url: string;
    icon: string;
}

interface ChecklistResponseDetail {
    id: number;
    checklist_name: string;
    checklist_description: string;
    user_name: string;
    total_score: number;
    max_possible_score: number;
    percentage: number;
    scoring_level: {
        key: string;
        label: string;
        color: string;
    };
    ai_report: string;
    recommended_services: RecommendedService[];
    status: string;
    completed_at: string;
    answers: Answer[];
}

const breadcrumbs = [
    { label: 'Inicio', path: '/intranet' },
    { label: 'Checklists', path: '/intranet/checklists' },
    { label: 'Resultado', isCurrent: true }
];

const ChecklistResult = () => {
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const { getResponseDetail, fetchingResponseDetail } = useChecklistService();
    
    const [response, setResponse] = useState<ChecklistResponseDetail | null>(null);
    const [showAnswers, setShowAnswers] = useState(false);

    useEffect(() => {
        if (id) {
            loadResponse(parseInt(id));
        }
    }, [id]);

    const loadResponse = (responseId: number) => {
        getResponseDetail(responseId, {
            onSuccess: (res: ServiceResponse) => {
                setResponse(res.data.response);
            },
            onError: (res: ServiceResponse) => {
                toast.error(res.message);
                history.push('/intranet/checklists');
            }
        });
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-CL', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Función para renderizar markdown básico a HTML
    const renderMarkdown = (text: string) => {
        if (!text) return '';
        
        // Primero se escapa todo: el informe lo genera una IA a partir de
        // respuestas del usuario y podría traer HTML/JS (XSS al abrirlo el admin).
        const escaped = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        // Convertir headers
        let html = escaped
            .replace(/^## (.*$)/gim, '<h5 class="fw-bold mt-4 mb-2">$1</h5>')
            .replace(/^# (.*$)/gim, '<h4 class="fw-bold mt-4 mb-2">$1</h4>')
            // Convertir negritas
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            // Convertir cursivas
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            // Convertir listas
            .replace(/^- (.*$)/gim, '<li>$1</li>')
            // Convertir saltos de línea
            .replace(/\n/g, '<br/>');
        
        // Envolver listas
        html = html.replace(/(<li>.*<\/li>)/g, '<ul class="mb-2">$1</ul>');
        
        return html;
    };

    if (fetchingResponseDetail || !response) {
        return (
            <div className="container-fluid">
                <div className="page-container">
                    <LazyLoading height={400} />
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="page-container">
                <PageHeader title="" breadcrumbs={breadcrumbs} />

                {/* Resultado Principal */}
                <DefaultCard>
                    <div className="text-center mb-4">
                        <h2 className="fw-bold mb-2">{response.checklist_name}</h2>
                        <p className="text-muted">Completado el {formatDate(response.completed_at)}</p>
                    </div>

                    {/* Score Circle */}
                    <div className="text-center mb-4">
                        {(() => {
                            const pct = Math.min(response.percentage, 100);
                            const color = response.scoring_level?.color || '#3b82f6';
                            return (
                                <div 
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                    style={{ 
                                        width: '150px', 
                                        height: '150px', 
                                        backgroundColor: color,
                                        boxShadow: `0 0 30px ${color}40`
                                    }}
                                >
                                    <div className="text-white text-center">
                                        <h1 className="mb-0 fw-bold">{pct}%</h1>
                                    </div>
                                </div>
                            );
                        })()}
                        <h4 
                            className="fw-bold mb-2"
                            style={{ color: response.scoring_level?.color || '#6c757d' }}
                        >
                            {response.scoring_level?.label || 'Sin clasificar'}
                        </h4>
                        <p className="text-muted">
                            Puntaje: {response.total_score} de {response.max_possible_score} puntos
                        </p>
                    </div>

                    {/* Barra de progreso visual */}
                    <div className="mb-5" style={{ maxWidth: '500px', margin: '0 auto' }}>
                        <div className="progress" style={{ height: '20px' }}>
                            <div 
                                className="progress-bar" 
                                style={{ 
                                    width: `${Math.min(response.percentage, 100)}%`,
                                    backgroundColor: response.scoring_level?.color || '#3b82f6'
                                }}
                            />
                        </div>
                    </div>
                </DefaultCard>

                {/* Informe de IA */}
                {response.ai_report && (
                    <DefaultCard>
                        <h4 className="fw-bold mb-4">
                            <i className="fas fa-robot me-2 text-primary"></i>
                            Informe Personalizado
                        </h4>
                        <div 
                            className="report-content"
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(response.ai_report) }}
                        />
                    </DefaultCard>
                )}

                {/* Botones de acción */}
                <div className="d-flex justify-content-center gap-3 mb-4">
                    <ButtonGeneral
                        onClick={() => history.push('/intranet/checklists')}
                        title="Volver a Checklists"
                        variant="outline"
                        size="small"
                        icon="fa-arrow-left"
                        iconPosition="left"
                    />
                    <ButtonGeneral
                        onClick={() => window.print()}
                        title="Imprimir Resultado"
                        variant="primary"
                        size="small"
                        icon="fa-print"
                        iconPosition="left"
                    />
                </div>
            </div>
        </div>
    );
};

export default ChecklistResult;

